/**
 * FunnelExperience
 *
 * Orchestrator component that evaluates the funnel step for a given page type
 * and renders the correct UI component (modal, banner, or popup).
 *
 * - onAccept (CTA button) = call accept API + add-to-cart, then dismiss
 * - onDismiss (X button) = temporary hide, reappears on next page visit
 * - onDecline ("No thanks") = call decline API, suppress for the rest of the session
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import styled from '@emotion/styled';
import { useFunnelExperience } from '../use-funnel-experience';
import type { FunnelPageType } from '../use-funnel-experience';
import { useFunnelContext } from '../funnel-provider';
import { extractBannerPlacement, resolveBannerContainerStyle } from '../banner-placement';
import { useFunnelAnalytics } from '../analytics';
import type { PageContext, StepPageContent, StepPageBlock, FunnelOffer, DisplaySettings } from '../types';
import { FunnelModal } from './FunnelModal';
import { FunnelBanner } from './FunnelBanner';
import { FunnelPopup } from './FunnelPopup';
import { FunnelPageContent } from './FunnelPageContent';
import { ActionExecutor } from './ActionExecutor';
import { ListenerBinder } from './ListenerBinder';
import { funnelLogger } from '../logger';

type TemplateLoadState = 'idle' | 'loading' | 'ready' | 'failed';

interface FunnelExperienceProps {
  pageType: FunnelPageType;
  context: Omit<PageContext, 'pageType'>;
  onAddToCart?: (productId: string, variantId: string, quantity: number) => void;
}

const SelectorWrap = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0 0 12px;
`;

const SelectorButton = styled.button<{ $selected: boolean }>`
  border: 1px solid ${({ theme, $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.border.primary};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.background.primary};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.background.primary : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export function FunnelExperience({ pageType, context, onAddToCart }: FunnelExperienceProps) {
  const { loading, experience, offer, experienceType, activeFunnelId, dismiss, decline, overrideExperience } =
    useFunnelExperience(pageType, context);
  const { acceptOffer, declineOffer, assignedFunnelId, fetchTemplate, setActiveExperience } = useFunnelContext();
  const analytics = useFunnelAnalytics();
  const funnelId = assignedFunnelId ?? activeFunnelId ?? '';
  const [accepting, setAccepting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

  const getActionErrorMessage = useCallback((error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }, []);

  useEffect(() => {
    setActionError(null);
  }, [experience?.id]);

  useEffect(() => {
    setSelectedOfferId(experience?.offers?.[0]?.id ?? experience?.offer?.id ?? null);
  }, [experience?.id, experience?.offers, experience?.offer?.id]);

  const activeOffer =
    (selectedOfferId ? experience?.offers?.find((item) => item.id === selectedOfferId) ?? null : null) ??
    experience?.offers?.[0] ??
    offer ??
    null;

  // Fetch step-level template content + displaySettings when templateId is set
  const [templateContent, setTemplateContent] = useState<StepPageBlock[] | null>(null);
  const [templateDisplaySettings, setTemplateDisplaySettings] = useState<DisplaySettings | null>(null);
  const [templateLoadState, setTemplateLoadState] = useState<TemplateLoadState>('idle');
  useEffect(() => {
    if (!experience?.templateId) {
      setTemplateContent(null);
      setTemplateDisplaySettings(null);
      setTemplateLoadState('idle');
      return;
    }
    // TF-AUD-005: Clear stale template content immediately when templateId
    // changes, so we don't briefly render the previous template's content.
    setTemplateContent(null);
    setTemplateDisplaySettings(null);
    setTemplateLoadState('loading');
    let cancelled = false;
    fetchTemplate(experience.templateId)
      .then((data) => {
        if (!cancelled) {
          if (data?.pageContent) {
            // pageContent from the template may be StepPageContent (with .blocks) or StepPageBlock[]
            const blocks = Array.isArray(data.pageContent)
              ? data.pageContent
              : (data.pageContent as StepPageContent).blocks ?? null;
            setTemplateContent(blocks);
            setTemplateLoadState('ready');
          } else {
            setTemplateLoadState('failed');
          }
          if (data?.displaySettings) {
            setTemplateDisplaySettings(data.displaySettings ?? null);
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplateLoadState('failed');
        funnelLogger.warn('FunnelExperience template fetch failed; using pageContent fallback', {
          templateId: experience.templateId ?? null,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => { cancelled = true; };
  }, [experience?.templateId, fetchTemplate]);

  const handleAccept = useCallback(async () => {
    if (!experience) return;
    setAccepting(true);
    setActionError(null);
    try {
      if (activeOffer) {
        const fallbackProductId = activeOffer.productId ?? context.productId;
        const fallbackVariantId = activeOffer.variantId ?? undefined;
        if (!fallbackProductId) {
          setAccepting(false);
          setActionError('This promotion is missing product information. Please contact support.');
          return;
        }
        analytics.trackOfferAccepted({
          funnelId,
          stepId: experience.id,
          offerId: activeOffer.id,
          offerName: activeOffer.name,
          productId: fallbackProductId,
        });

        void acceptOffer({
          stepId: experience.id,
          productId: fallbackProductId ?? activeOffer.id,
          variantId: fallbackVariantId,
          quantity: 1,
          action: 'add_to_cart',
        }).catch((err) => {
          funnelLogger.warn('FunnelExperience accept API failed; continuing cart flow', {
            stepId: experience.id,
            funnelId,
            error: err instanceof Error ? err.message : String(err),
          });
        });

        // 2. Add to cart via storefront cart integration
        if (onAddToCart && fallbackProductId) {
          analytics.trackAddToCart({
            productId: fallbackProductId,
            variantId: fallbackVariantId ?? '',
            quantity: 1,
            source: 'funnel_offer',
          });
          await onAddToCart(fallbackProductId, fallbackVariantId ?? '', 1);
        }
      }
      // Success — dismiss the experience
      setAccepting(false);
      dismiss();
    } catch (err) {
      funnelLogger.error('[FunnelExperience] accept failed', err instanceof Error ? err : undefined, {
        stepId: experience.id,
        funnelId,
      });
      setAccepting(false);
      setActionError(getActionErrorMessage(err, 'We could not process this purchase right now. Please try again.'));
    }
  }, [experience, activeOffer, acceptOffer, onAddToCart, dismiss, analytics, funnelId, getActionErrorMessage]);

  const handleDecline = useCallback(async () => {
    if (!experience) {
      decline();
      return;
    }
    setActionError(null);
    try {
      analytics.trackOfferDeclined({
        funnelId,
        stepId: experience.id,
        offerId: activeOffer?.id,
      });
      // Call decline API (records event, returns downsell step if any)
      const nav = await declineOffer(experience.id);

      if (nav.nextStep) {
        // Downsell chaining: swap in the next step without dismissing.
        // The current step's id is already persisted to sessionStorage by
        // the decline() call inside overrideExperience's sibling path; here
        // we only mark the original step declined and show the downsell.
        overrideExperience(nav.nextStep);
        return;
      }
    } catch (err) {
      funnelLogger.error('[FunnelExperience] decline failed', err instanceof Error ? err : undefined, {
        stepId: experience.id,
        funnelId,
      });
      setActionError(getActionErrorMessage(err, 'We could not process your response right now. Please try again.'));
      return;
    }
    decline();
  }, [experience, declineOffer, decline, overrideExperience, analytics, funnelId, activeOffer?.id, getActionErrorMessage]);

  const handleDismiss = useCallback(() => {
    if (experience) {
      analytics.trackExperienceDismissed({ funnelId, stepId: experience.id });
    }
    dismiss();
  }, [dismiss, experience, funnelId, analytics]);

  // Track experience shown (fire once per step)
  const trackedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!experience || trackedStepRef.current === experience.id) return;
    trackedStepRef.current = experience.id;
    analytics.trackExperienceShown({
      funnelId,
      stepId: experience.id,
      stepName: experience.name,
      experienceType: experienceType ?? undefined,
      placement: experience.placement ?? undefined,
    });
  }, [experience, experienceType, funnelId, analytics]);

  // TF-AUD-005: When templateId is set, template content ALWAYS wins (live
  // templates). pageContent is preserved as fallback for when template fetch
  // fails or templateId is cleared.
  const effectivePageContent: StepPageContent | StepPageBlock[] | null =
    experience?.templateId
      ? (templateContent ?? experience?.pageContent ?? null)
      : (experience?.pageContent ?? templateContent);

  // Nothing to show — render if we have an offer OR page content with blocks
  const hasPageContent = (() => {
    if (!effectivePageContent) return false;
    if (Array.isArray(effectivePageContent)) return effectivePageContent.length > 0;
    return (Array.isArray(effectivePageContent.blocks) && effectivePageContent.blocks.length > 0) ||
           (Array.isArray(effectivePageContent.components) && effectivePageContent.components.length > 0);
  })();

  // TF-209: Extract actions early to check if we should render even without offer/pageContent
  const stepActions = experience?.actions;
  const hasActions = stepActions && stepActions.length > 0;

  // TF-210: Extract listeners early so the guard can account for 'none' type with listeners
  const stepListeners = experience?.listeners;
  const hasListeners = stepListeners && stepListeners.length > 0;

  useEffect(() => {
    if (!experience?.templateId || templateLoadState === 'idle' || templateLoadState === 'loading') {
      return;
    }

    if (templateLoadState === 'ready') {
      return;
    }

    const fallbackMode =
      hasPageContent ? 'step_page_content'
      : activeOffer ? 'hardcoded_offer_layout'
      : hasActions || hasListeners ? 'action_only'
      : 'nothing_renderable';

    funnelLogger.warn('FunnelExperience template fallback activated', {
      stepId: experience.id,
      funnelId,
      templateId: experience.templateId,
      reason: 'template_fetch_failed_or_empty',
      fallbackMode,
      experienceType: experienceType ?? null,
      hasStepPageContent: Boolean(experience.pageContent),
      hasOffer: Boolean(activeOffer),
      hasActions: Boolean(hasActions),
      hasListeners: Boolean(hasListeners),
    });
  }, [
    experience?.id,
    experience?.templateId,
    experience?.pageContent,
    funnelId,
    templateLoadState,
    hasPageContent,
    activeOffer,
    hasActions,
    hasListeners,
    experienceType,
  ]);

  // Log which rendering case is active on mount (dev only, section 16 of step-template-relationship.md)
  useEffect(() => {
    if (!experience) return;
    const templateId = experience.templateId;
    const hasPC = Boolean(hasPageContent);
    const actCount = stepActions?.length ?? 0;
    const renderCase =
      templateId && hasPC ? 'C' :
      templateId ? 'A' :
      hasPC ? 'B' :
      actCount > 0 ? 'D' : 'E';
    if (process.env.NODE_ENV === 'development') {
      funnelLogger.info('FunnelExperience render case', {
        stepId: experience.id,
        renderCase,
        templateId: templateId ?? null,
        pageContent: hasPC,
        actions: actCount,
        experienceType: experienceType ?? null,
      });
    }
  }, [experience, hasPageContent, stepActions, experienceType]);

  const isTemplatePending =
    Boolean(experience?.templateId) &&
    !templateContent &&
    templateLoadState !== 'failed';

  // Keep hook ordering stable across template loading/runtime transitions.
  // The effect itself decides whether there is an active visible experience.
  const triggerLifecycle = experience?.triggerLifecycle ?? 'session';
  const displaySettings = experience?.displaySettings ?? templateDisplaySettings ?? undefined;
  const bannerPlacement = experienceType === 'banner' ? extractBannerPlacement(displaySettings) : null;
  const bannerContainerStyle = experienceType === 'banner'
    ? (resolveBannerContainerStyle(bannerPlacement) as CSSProperties | undefined)
    : undefined;
  const experienceOffers = experience?.offers;
  const multiOffers = Array.isArray(experienceOffers) && experienceOffers.length > 1 ? experienceOffers : null;

  useEffect(() => {
    if (
      loading ||
      isTemplatePending ||
      !experience ||
      !experienceType ||
      (!activeOffer && !hasPageContent && !hasActions && !hasListeners)
    ) {
      setActiveExperience(null);
      return;
    }

    setActiveExperience({
      funnelId,
      stepId: experience.id,
      experienceType,
      placement: experience.placement,
      displaySettings,
      bannerPlacement,
    });

    return () => {
      setActiveExperience(null);
    };
  }, [
    loading,
    isTemplatePending,
    experience,
    experienceType,
    funnelId,
    activeOffer,
    hasPageContent,
    hasActions,
    hasListeners,
    displaySettings,
    bannerPlacement,
    setActiveExperience,
  ]);

  if (isTemplatePending) {
    if (experienceType === 'page') {
      return <div aria-live="polite">Loading page...</div>;
    }
    return null;
  }

  if (loading || !experience || (!activeOffer && !hasPageContent && !hasActions && !hasListeners)) {
    return null;
  }

  // Provide a safe fallback offer for page-content-only experiences
  const safeOffer: FunnelOffer = activeOffer ?? {
    id: '',
    name: '',
    productId: '',
    variantId: '',
    headline: '',
    description: '',
    imageUrl: '',
    ctaText: 'Shop Now',
    declineText: 'No thanks',
    pricingType: 'fixed',
    pricingValue: null,
    originalPrice: null,
  };

  // TF-AUD-004: frequencyCap controls experience visibility (step-level),
  // triggerLifecycle controls action/listener firing (funnel-level). They are
  // separate concerns with incompatible value sets — never conflate them.

  let experienceElement: React.ReactNode = null;

  switch (experienceType) {
    case 'modal':
      experienceElement = (
        <FunnelModal
          offer={safeOffer}
          pageContent={effectivePageContent}
          displaySettings={displaySettings}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
        />
      );
      break;
    case 'banner':
      experienceElement = (
        <FunnelBanner
          offer={safeOffer}
          pageContent={effectivePageContent}
          displaySettings={displaySettings}
          containerStyle={bannerContainerStyle}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
        />
      );
      break;
    case 'popup':
      experienceElement = (
        <FunnelPopup
          offer={safeOffer}
          pageContent={effectivePageContent}
          displaySettings={displaySettings}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
        />
      );
      break;
    case 'page':
      experienceElement = (
        <FunnelPageContent
          offer={safeOffer}
          offers={experience.offers}
          selectedOfferId={selectedOfferId ?? undefined}
          onSelectOffer={setSelectedOfferId}
          pageContent={effectivePageContent}
          displaySettings={displaySettings}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
        />
      );
      break;
    case 'none':
      // No visual experience — only actions and listeners run
      experienceElement = null;
      break;
    default:
      experienceElement = null;
      break;
  }

  return (
    <>
      {experienceType !== 'page' && multiOffers && (
        <SelectorWrap aria-label="Offer selector">
          {multiOffers.map((item, index) => {
            const isSelected = (selectedOfferId ?? safeOffer.id) === item.id || (!selectedOfferId && index === 0);
            return (
              <SelectorButton
                key={item.id}
                type="button"
                $selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => setSelectedOfferId(item.id)}
              >
                {item.name || item.headline || `Offer ${index + 1}`}
              </SelectorButton>
            );
          })}
        </SelectorWrap>
      )}
      {experienceElement}
      {stepActions && stepActions.length > 0 && (
        <ActionExecutor
          actions={stepActions}
          stepId={experience.id}
          triggerLifecycle={triggerLifecycle}
          onAddToCart={onAddToCart}
          fallbackOffer={activeOffer ?? undefined}
          onOfferAccept={async (offerId) => {
            setActionError(null);
            try {
              const resolvedActionOffer =
                activeOffer?.id === offerId
                  ? activeOffer
                  : experience.offers?.find((item) => item.id === offerId) ?? activeOffer ?? null;
              const fallbackProductId = resolvedActionOffer?.productId ?? context.productId;
              const fallbackVariantId = resolvedActionOffer?.variantId ?? undefined;

              await acceptOffer({
                stepId: experience.id,
                productId: fallbackProductId ?? offerId,
                variantId: fallbackVariantId,
                quantity: 1,
                action: 'add_to_cart',
              });

              if (onAddToCart && fallbackProductId) {
                analytics.trackAddToCart({
                  productId: fallbackProductId,
                  variantId: fallbackVariantId ?? '',
                  quantity: 1,
                  source: 'funnel_offer',
                });
                await onAddToCart(fallbackProductId, fallbackVariantId ?? '', 1);
              }
            } catch (err) {
              funnelLogger.error('[FunnelExperience] display_offer accept failed', err instanceof Error ? err : undefined, {
                stepId: experience.id,
                funnelId,
              });
              const message = getActionErrorMessage(err, 'We could not process this purchase right now. Please try again.');
              setActionError(message);
              throw err instanceof Error ? err : new Error(message);
            }
          }}
          onOfferDecline={async () => {
            setActionError(null);
            try {
              await declineOffer(experience.id);
            } catch (err) {
              funnelLogger.error('[FunnelExperience] display_offer decline failed', err instanceof Error ? err : undefined, {
                stepId: experience.id,
                funnelId,
              });
              const message = getActionErrorMessage(err, 'We could not process your response right now. Please try again.');
              setActionError(message);
              throw err instanceof Error ? err : new Error(message);
            }
          }}
        />
      )}
      {stepListeners && stepListeners.length > 0 && (
        <ListenerBinder
          listeners={stepListeners}
          stepId={experience.id}
          triggerLifecycle={triggerLifecycle}
        />
      )}
    </>
  );
}
